/**
 * Shared Recharts theme palette. These six colors were copy-pasted as inline
 * `isDark ? … : …` ternaries across every chart (usage + statistics); they are
 * the tooltip surface, axis ticks, and grid lines. Centralized here so the
 * chart chrome stays consistent and tweaks land in one place.
 *
 * Light/dark are returned as frozen module-level objects, so call sites can do
 * `getChartTheme(isDark).gridLine` with no per-render allocation. Per-chart
 * one-offs (cursor fill, series fills) stay inline in their component — only
 * the shared chrome lives here.
 */
export interface ChartTheme {
  /** Axis tick label color. */
  axisText: string;
  /** Cartesian grid line stroke. */
  gridLine: string;
  /** Tooltip surface background. */
  tooltipBg: string;
  /** Tooltip surface border. */
  tooltipBorder: string;
  /** Muted label/secondary text inside the tooltip. */
  tooltipMuted: string;
  /** Primary text inside the tooltip. */
  tooltipText: string;
}

const DARK_CHART_THEME: ChartTheme = {
  axisText: "rgba(255,255,255,0.45)",
  gridLine: "rgba(255,255,255,0.06)",
  tooltipBg: "#1f2937",
  tooltipBorder: "rgba(52,211,153,0.18)",
  tooltipMuted: "rgba(255,255,255,0.50)",
  tooltipText: "#f9fafb",
};

const LIGHT_CHART_THEME: ChartTheme = {
  axisText: "#9ca3af",
  gridLine: "#f3f4f6",
  tooltipBg: "#ffffff",
  tooltipBorder: "#cbd5e1",
  tooltipMuted: "#6b7280",
  tooltipText: "#111827",
};

export function getChartTheme(isDark: boolean): ChartTheme {
  return isDark ? DARK_CHART_THEME : LIGHT_CHART_THEME;
}
